import { useState } from 'react';
import type { ResponseDetail, ResponseSource, CompetitorMention, KeyClaim, CaptureMode } from '../types/index.js';
import { Icon } from './ui/Icon.js';
import { MeasureBar } from './ui/MeasureBar.js';
import { SourcesMethodologyNote, BodyParsedSourcesNote } from './CaptureProvenance.js';

interface AnalysisPanelProps {
  response: ResponseDetail;
}

/** Domain type badge colors matching ResponseContent */
const DOMAIN_TYPE_COLORS: Record<string, { bg: string; text: string }> = {
  your: { bg: 'bg-green-100', text: 'text-green-800' },
  competitor: { bg: 'bg-red-100', text: 'text-red-800' },
  editorial: { bg: 'bg-blue-100', text: 'text-blue-800' },
  institutional: { bg: 'bg-purple-100', text: 'text-purple-800' },
  corporate: { bg: 'bg-amber-100', text: 'text-amber-800' },
  reference: { bg: 'bg-gray-200', text: 'text-gray-700' },
  ugc: { bg: 'bg-pink-100', text: 'text-pink-800' },
  other: { bg: 'bg-gray-100', text: 'text-gray-600' },
};

function DomainTypeBadge({ type }: { type: string | null }) {
  const t = type ?? 'other';
  const colors = DOMAIN_TYPE_COLORS[t] ?? DOMAIN_TYPE_COLORS.other;
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${colors.bg} ${colors.text}`}>
      {t}
    </span>
  );
}

const MENTION_CONTEXT_COLORS: Record<string, { bg: string; text: string }> = {
  recommended: { bg: 'bg-green-100', text: 'text-green-800' },
  compared: { bg: 'bg-blue-100', text: 'text-blue-800' },
  neutral: { bg: 'bg-gray-100', text: 'text-gray-700' },
  negative: { bg: 'bg-red-100', text: 'text-red-800' },
  not_mentioned: { bg: 'bg-gray-50', text: 'text-gray-500' },
};

function MentionContextBadge({ context }: { context: string | null }) {
  if (!context) return null;
  const colors = MENTION_CONTEXT_COLORS[context] ?? MENTION_CONTEXT_COLORS.neutral;
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${colors.bg} ${colors.text}`}>
      {context.replace('_', ' ')}
    </span>
  );
}

/**
 * Spec 234 §5: a light column for the range, an ink dash at the value. This
 * is the only bar in this widget, so it is `emphasize`d — the one mark that
 * carries the point takes the action colour. Sentiment carries a status
 * meaning too (positive/neutral/negative), but that's already said in
 * words by `label`; the bar is a measurement, not a second status chip.
 */
function SentimentBar({ score, label }: { score: number | null; label: string | null }) {
  if (score === null && !label) return <span className="text-xs text-gray-400">No sentiment data</span>;
  // score null with a label present is a pre-existing edge case (label
  // without a numeric score) — the dash sits at the neutral midpoint rather
  // than vanishing, same fallback the old fixed-50% bar used.
  const fraction = score !== null ? (score + 1) / 2 : 0.5;

  return (
    <div className="flex items-center gap-2">
      <MeasureBar fraction={fraction} emphasize className="w-20" />
      <span className="text-xs text-gray-600 capitalize">{label ?? 'unknown'}</span>
      {score !== null && <span className="text-xs text-gray-400">({score.toFixed(2)})</span>}
    </div>
  );
}

function AccuracyBadge({ accuracy }: { accuracy: string | null }) {
  if (!accuracy) return <Icon name="bx-help-circle" className="text-gray-400" />;
  if (accuracy === 'accurate') return <Icon name="bx-check" className="text-green-600" />;
  if (accuracy === 'inaccurate') return <Icon name="bx-x" className="text-red-600" />;
  return <Icon name="bx-help-circle" className="text-yellow-500" />;
}

function Section({ title, count, children, defaultOpen = true, testId }: {
  title: string;
  count?: number;
  children: React.ReactNode;
  defaultOpen?: boolean;
  testId?: string;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="border-b border-gray-100 last:border-b-0" data-testid={testId}>
      <button
        className="flex w-full items-center justify-between py-3 text-left"
        onClick={() => setOpen(!open)}
      >
        <span className="text-sm font-semibold text-gray-900">
          {title}
          {count !== undefined && (
            <span className="ml-1.5 text-xs font-normal text-gray-400">({count})</span>
          )}
        </span>
        {open ? <Icon name="bx-chevron-down" className="text-gray-400" /> : <Icon name="bx-chevron-right" className="text-gray-400" />}
      </button>
      {open && <div className="pb-3">{children}</div>}
    </div>
  );
}

function BrandsSection({ response }: { response: ResponseDetail }) {
  const analysis = response.analysis;
  if (!analysis) {
    return (
      <Section title="Brands" testId="brands-section">
        <p className="text-xs text-gray-400">Analysis not yet available</p>
      </Section>
    );
  }

  const { entity_extraction, classification } = analysis;

  return (
    <Section title="Brands" testId="brands-section">
      <div className="space-y-3">
        {/* Primary brand */}
        <div className="rounded-md bg-gray-50 p-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-900">
              {entity_extraction.brand_mentioned ? 'Brand detected' : 'Not mentioned'}
            </span>
            {entity_extraction.mention_position !== null && (
              <span className="text-sm font-semibold text-violet-600">#{entity_extraction.mention_position}</span>
            )}
          </div>
          <div className="mt-1 flex items-center gap-2">
            <MentionContextBadge context={entity_extraction.mention_context} />
            <SentimentBar score={classification.sentiment_score} label={classification.sentiment_label} />
          </div>
        </div>

        {/* Competitor mentions */}
        {entity_extraction.competitor_mentions && entity_extraction.competitor_mentions.length > 0 && (
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">Competitors</p>
            <div className="space-y-1">
              {entity_extraction.competitor_mentions.map((cm: CompetitorMention, i: number) => (
                <div key={i} className="flex items-center justify-between text-xs text-gray-600 rounded-md bg-gray-50 px-2 py-1">
                  <span>{cm.brand_name}</span>
                  <div className="flex items-center gap-2">
                    {cm.position !== null && <span className="text-violet-600">#{cm.position}</span>}
                    {cm.context && <MentionContextBadge context={cm.context} />}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Section>
  );
}

function SourcesSection({
  sources,
  captureMode,
  sourcesReturnedByEngine,
  sourcesExtractedByBeacon,
}: {
  sources: ResponseSource[];
  captureMode?: CaptureMode;
  sourcesReturnedByEngine?: number | null;
  sourcesExtractedByBeacon?: number | null;
}) {
  return (
    <Section title="Sources" count={sources.length} testId="sources-section">
      <div className="mb-2 space-y-1">
        <SourcesMethodologyNote captureMode={captureMode} />
        {/* Spec 160 — explain a NULL engine-source count next to a non-empty list. */}
        <BodyParsedSourcesNote
          sourcesReturnedByEngine={sourcesReturnedByEngine}
          sourcesExtractedByBeacon={sourcesExtractedByBeacon}
          displayedSourceCount={sources.length}
        />
      </div>
      {sources.length === 0 ? (
        <p className="text-xs text-gray-400">No sources extracted</p>
      ) : (
        <div className="space-y-2">
          {sources.map((s) => (
            <div key={s.id} className="rounded-md border border-gray-100 p-2 text-xs">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className="font-medium text-gray-900 truncate">
                    {s.domain_display_name ?? s.domain ?? 'Unknown'}
                  </span>
                  <DomainTypeBadge type={s.domain_type} />
                </div>
                <span className="text-gray-400 shrink-0">#{s.position}</span>
              </div>
              {/* Retrieval / Citation indicators */}
              <div className="mt-1 flex items-center gap-3">
                <span className={`flex items-center gap-0.5 ${s.is_cited ? 'text-green-600' : 'text-gray-400'}`}>
                  {s.is_cited ? <Icon name="bx-check" /> : <Icon name="bx-minus" />}
                  Cited
                </span>
                <span className={`flex items-center gap-0.5 ${s.is_retrieved ? 'text-blue-600' : 'text-gray-400'}`}>
                  {s.is_retrieved ? <Icon name="bx-check" /> : <Icon name="bx-minus" />}
                  Retrieved
                </span>
              </div>
              {/* URL */}
              {s.url && (
                <a
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1 flex items-center gap-1 text-violet-600 hover:underline truncate"
                >
                  <Icon name="bx-link-external" />
                  {s.url.length > 60 ? s.url.substring(0, 60) + '...' : s.url}
                </a>
              )}
              {/* Snippet */}
              {s.snippet && (
                <p className="mt-1 text-gray-500 line-clamp-2">{s.snippet}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

function ClaimsSection({ claims }: { claims: KeyClaim[] }) {
  if (!claims || claims.length === 0) {
    return (
      <Section title="Key claims" testId="claims-section">
        <p className="text-xs text-gray-400">No claims detected</p>
      </Section>
    );
  }

  return (
    <Section title="Key claims" count={claims.length} testId="claims-section">
      <div className="space-y-1.5">
        {claims.map((c, i) => (
          <div key={i} className="flex items-start gap-2 text-xs">
            <AccuracyBadge accuracy={c.accuracy} />
            <span className="text-gray-700">{c.claim}</span>
          </div>
        ))}
      </div>
    </Section>
  );
}

function FanoutSection({ aioSources }: { aioSources: unknown[] | null }) {
  if (!aioSources || aioSources.length === 0) return null;

  return (
    <Section title="Fan-out queries" count={aioSources.length} testId="fanout-section">
      <div className="space-y-1">
        {aioSources.map((source, i) => (
          <div key={i} className="rounded-md bg-gray-50 px-2 py-1 text-xs text-gray-600">
            {typeof source === 'string' ? source : JSON.stringify(source)}
          </div>
        ))}
      </div>
    </Section>
  );
}

function RawAnalysisSection({ response }: { response: ResponseDetail }) {
  const rawData = {
    analysis_status: response.analysis_status,
    stage_statuses: response.stage_statuses,
    analysis: response.analysis,
    sources_count: response.sources.length,
  };

  return (
    <Section title="Raw analysis" defaultOpen={false} testId="raw-analysis-section">
      <pre className="max-h-80 overflow-auto rounded-md bg-gray-900 p-3 text-xs text-green-400">
        {JSON.stringify(rawData, null, 2)}
      </pre>
    </Section>
  );
}

export function AnalysisPanel({ response }: AnalysisPanelProps) {
  const claims = response.analysis?.classification?.key_claims ?? [];

  return (
    <div className="flex flex-col" data-testid="analysis-panel">
      <h2 className="text-sm font-semibold text-gray-900 pb-2 border-b border-gray-200">Analysis</h2>
      <div className="mt-2">
        <BrandsSection response={response} />
        <SourcesSection
          sources={response.sources}
          captureMode={response.capture_mode}
          sourcesReturnedByEngine={response.sources_returned_by_engine}
          sourcesExtractedByBeacon={response.sources_extracted_by_beacon}
        />
        <ClaimsSection claims={claims} />
        <FanoutSection aioSources={response.aio_sources} />
        <RawAnalysisSection response={response} />
      </div>
    </div>
  );
}
